import { useCallback, useContext, useMemo } from "react";
// contexts
import { CartContext } from "@cart-client/contexts/CartContext";
import { AnalyticsContext } from "@swiggy-private/checkout-core";
// helpers
import { buildRXListIdentifierForMROCart, getMROChildCartFromLocalCartByRxId } from "@cart-client/helpers/MROFoodCart";
import { clickMROCouponClashPopup, impressionMROCouponClashPopup } from "@cart-client/analytics/events/CouponListing";
// types
import { MROReviewedCart } from "@cart-client/interfaces/MROFoodCart";
import { MROAnalyticsPayload } from "@cart-client/interfaces/Coupon";
import { MRONativeLocalCart } from "@swiggy-private/cart-types";

export const useMROAnalyticsPayload = () => {
    const { impressionEvent, clickEvent } = useContext(AnalyticsContext);
    const {
        cartState: { localCart, reviewedCart },
    } = useContext(CartContext);

    const getMROAnalyticsPayload = useCallback(
        (rxId?: string): MROAnalyticsPayload => {
            const childCartForRXId = getMROChildCartFromLocalCartByRxId(localCart as MRONativeLocalCart, rxId);
            return {
                parentCartId: String(reviewedCart?.data?.cartId || ""),
                selectedRXList: buildRXListIdentifierForMROCart(reviewedCart as MROReviewedCart) || "",
                selectedRxId: rxId || "",
                childCartId: childCartForRXId?.cartId || "",
            };
        },
        [localCart, reviewedCart],
    );

    const logImpressionMROCouponClashPopup = useCallback(
        (rxId?: string) => {
            const payload = getMROAnalyticsPayload(rxId);
            impressionMROCouponClashPopup({ impressionEvent, payload });
        },
        [impressionEvent, getMROAnalyticsPayload],
    );

    const logClickMROCouponClashPopup = useCallback(
        (rxId?: string, text = "") => {
            const payload = getMROAnalyticsPayload(rxId);
            clickMROCouponClashPopup({ clickEvent, payload, text });
        },
        [getMROAnalyticsPayload, clickEvent],
    );

    const payload = useMemo(() => {
        return {
            getMROAnalyticsPayload,
            analytics: {
                logImpressionMROCouponClashPopup,
                logClickMROCouponClashPopup,
            },
        };
    }, [getMROAnalyticsPayload, logImpressionMROCouponClashPopup, logClickMROCouponClashPopup]);

    return payload;
};
